/**
 * ACA subsidy planner — Phase 7 Session 1.
 *
 * **Purpose**: model the ACA premium tax credit (PTC) for an early-
 * retiree household choosing a Marketplace plan. The PTC formula is
 *
 *     subsidy = max(0, SLCSP_premium - MAGI × applicable_pct)
 *
 * where `applicable_pct` is read from the ACA curve at the household's
 * `MAGI / FPL × 100` percentage. Hearth's helper computes the
 * applicable percentage + the household's expected premium share
 * (what the household pays out-of-pocket for the SLCSP) + the
 * "distance to cliff" — critical for early retirees whose MAGI sits
 * close to the 400% FPL boundary.
 *
 * **What this DOES NOT compute**:
 *   - The actual SLCSP_premium for the household's geography. SLCSP
 *     varies wildly by zip code; users look it up at healthcare.gov
 *     or via their state Marketplace. v1 just outputs the
 *     `expected_premium_share_cents` (what the household owes) — caller
 *     subtracts from SLCSP to get the subsidy.
 *   - Cost-Sharing Reductions (CSRs) for households < 250% FPL.
 *     Phase 11 polish.
 *   - State-specific subsidy add-ons (CA, MA, NY, NJ, etc.). Phase
 *     11 / 15.
 *
 * **Authority** (all retrieved 2026-05-08):
 *   - IRC § 36B (premium tax credit)
 *   - ARPA § 9661 + IRA § 12001 (enhanced subsidies through 2025)
 *   - IRS Pub 974 (Premium Tax Credit guide)
 *
 * **Web mirror** at `web/src/lib/util/aca.ts`.
 */

import {
  ACA_CURVE_IRA_EXTENDED,
  ACA_CURVE_PRE_ARPA,
  applicablePercentage,
  type AcaPercentageCurve,
  type AcaPercentageCurveKind,
} from "../data/aca-applicable-percentages.js";
import {
  fplFor,
  type AcaJurisdiction,
} from "../data/aca-fpl-2025.js";

export type AcaSunsetAssumption = AcaPercentageCurveKind;

export interface AcaSubsidyInput {
  /** Modified Adjusted Gross Income (MAGI) for ACA purposes. For most
   *  filers this is AGI + tax-exempt interest + foreign-earned-income
   *  exclusion + Social Security benefits not in AGI. */
  readonly magi_cents: number;
  /** Household members (the "tax family") on the Marketplace plan.
   *  Should match the household_size used for FPL lookup. */
  readonly household_size: number;
  /** Geographic jurisdiction for FPL — most households use
   *  `contiguous_48_dc`. AK + HI have higher FPL thresholds. */
  readonly jurisdiction?: AcaJurisdiction;
  /** Sunset assumption — `ira_extended` (assumes Congress keeps the
   *  enhanced subsidies past 2025) or `pre_arpa_baseline` (current law
   *  reverts in 2026). Hearth's UI defaults to ira_extended but lets
   *  the user toggle. */
  readonly sunset_assumption?: AcaSunsetAssumption;
}

export interface AcaSubsidyResult {
  readonly your_magi_cents: number;
  readonly fpl_at_100_cents: number;
  readonly fpl_at_150_cents: number;
  readonly fpl_at_400_cents: number;
  /** Where MAGI sits as a percentage of FPL. */
  readonly your_fpl_pct: number;
  /** Applicable percentage at `your_fpl_pct` per the chosen curve.
   *  -1 means the household exceeded the cliff (pre-ARPA only) — no
   *  subsidy at all. */
  readonly applicable_pct: number;
  /** What the household owes for the SLCSP at this MAGI. The actual
   *  subsidy = SLCSP_premium - this. = -1 when above cliff. */
  readonly expected_premium_share_cents: number;
  /** Distance to the 400% FPL cliff in cents (positive = headroom). */
  readonly distance_to_cliff_cents: number;
  /** Cliff threshold in FPL %. */
  readonly cliff_at_pct: number;
  /** Curve used for the calculation (echoed back for UI display). */
  readonly sunset_assumption: AcaSunsetAssumption;
  /** True iff above the cliff under pre-ARPA assumptions. */
  readonly above_cliff: boolean;
}

export function acaSubsidy(input: AcaSubsidyInput): AcaSubsidyResult {
  const jurisdiction: AcaJurisdiction =
    input.jurisdiction ?? "contiguous_48_dc";
  const assumption: AcaSunsetAssumption =
    input.sunset_assumption ?? "ira_extended";

  const fpl_100 = fplFor(input.household_size, jurisdiction);
  const fpl_150 = Math.round(fpl_100 * 1.5);
  const fpl_400 = Math.round(fpl_100 * 4.0);

  const your_fpl_pct =
    fpl_100 > 0
      ? Math.round((input.magi_cents / fpl_100) * 10000) / 100
      : 0;

  const curve: AcaPercentageCurve =
    assumption === "ira_extended"
      ? ACA_CURVE_IRA_EXTENDED
      : ACA_CURVE_PRE_ARPA;

  const applicable_pct = applicablePercentage(your_fpl_pct, curve);
  const above_cliff = applicable_pct === -1;
  const expected_premium_share_cents = above_cliff
    ? -1
    : Math.round((input.magi_cents * applicable_pct) / 100);

  const distance_to_cliff_cents = fpl_400 - input.magi_cents;

  return {
    your_magi_cents: input.magi_cents,
    fpl_at_100_cents: fpl_100,
    fpl_at_150_cents: fpl_150,
    fpl_at_400_cents: fpl_400,
    your_fpl_pct,
    applicable_pct,
    expected_premium_share_cents,
    distance_to_cliff_cents,
    cliff_at_pct: curve.cliff_at_fpl_pct,
    sunset_assumption: assumption,
    above_cliff,
  };
}
